import React, { useEffect, useState } from 'react';
import { Grid2 as Grid, Box, Button, useMediaQuery } from '@mui/material';
import { Delete, Edit, Save, Undo } from '@mui/icons-material';
import { doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Actions, useDispatchProvider, useStateFormUser } from '../utils/StateProvider';
import { useForm } from 'react-hook-form';
import SkeletonForm from '../utils/SkeletonForm';
import FieldText from '../fields/FieldText';
import { createPortal } from 'react-dom';
import { usePermissions } from '../utils/AclProvider';
import FieldImage from '../fields/FieldImage';
import FieldSelect from '../fields/FieldSelect';
import { userRoles } from '../../data/users';
import FieldCurrency from '../fields/FieldCurrency';

const FormUser = ({ portalRef = null }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const permissions = usePermissions();
  const stateFormUser = useStateFormUser();
  const dispatch = useDispatchProvider();
  const { control, getValues, reset, setValue, watch } = useForm({
    defaultValues: {
      displayName: '',
      dateSignIn: '',
      email: '',
      photoURL: '',
      role: '',
      sequenceTotal: 0,
    },
  });

  const [stateForm, setStateForm] = useState('create');
  const [stateLoading, setStateLoading] = useState(true);
  const [stateSaving, setStateSaving] = useState(false);

  const userDocRef = stateFormUser ? doc(db, 'users', stateFormUser) : null;

  const fetchUser = async () => {
    if (stateFormUser === '') {
      reset();
      return;
    }

    try {
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        setStateForm('edit');
        // console.log('edit', docSnap.data());
        // setValue(docSnap.data());
        const customerData = docSnap.data();
        setValue('displayName', customerData.displayName);
        setValue('dateSignIn', customerData.dateSignIn);
        setValue('email', customerData.email);
        setValue('photoURL', customerData.photoURL);
        setValue('role', customerData.role);
        setValue('sequenceTotal', customerData.sequenceTotal);
      } else {
        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: {
              open: true,
              severity: 'error',
              message: `No user found!`,
            },
          });
      }
    } catch (err) {
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error fetching user: ${err.message}`,
          },
        });
    }
  };

  const saveUser = async () => {
    try {
      setStateSaving(true);
      const userForm = getValues();
      delete userForm.dateCreated;
      delete userForm.userCreated;
      await updateDoc(userDocRef, {
        ...userForm,
      }).then(() => {
        setStateSaving(false);
        setStateForm('edit');
        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: {
              open: true,
              severity: 'success',
              message: `Successfully update user: ${userForm.displayName}`,
            },
          });
      });
    } catch (err) {
      setStateSaving(false);
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error saving new user: ${err.message}`,
          },
        });
    }
  };

  const editUser = () => {
    setStateForm('save');
  };

  const revertUser = () => {
    setStateForm('edit');
    fetchUser().then();
  };

  const handleDialogDelete = () => {
    dispatch &&
      dispatch({
        type: Actions.DialogDelete,
        payload: {
          open: true,
          title: 'Are you sure you want to delete this user?',
          onConfirm: deleteUser,
        },
      });
  };

  const deleteUser = async () => {
    try {
      await deleteDoc(userDocRef).then(() => {
        if (dispatch) {
          dispatch({
            type: Actions.DialogDelete,
            payload: {
              open: false,
              title: '',
              onConfirm: () => {},
            },
          });
          dispatch({
            type: Actions.DrawerCustomer,
            payload: false,
          });
          dispatch({
            type: Actions.Snackbar,
            payload: {
              open: true,
              severity: 'success',
              message: 'Successfully user deleted.',
            },
          });
          dispatch({
            type: Actions.FormUser,
            payload: '',
          });
        }
      });
    } catch (err) {
      dispatch({
        type: Actions.Snackbar,
        payload: {
          open: true,
          severity: 'error',
          message: `Error deleting user: ${err}`,
        },
      });
    }
  };

  useEffect(() => {
    setStateLoading(true);
    fetchUser().then(() => {
      setStateLoading(false);
    });
  }, [stateFormUser]);

  return (
    <Box component={'form'}>
      <>
        {stateLoading ? (
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }}>
              <SkeletonForm />
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            spacing={3}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Grid
                container
                spacing={3}
              >
                <Grid size={12}>
                  <FieldText
                    control={control}
                    name={'displayName'}
                    label={'Name'}
                    disabled={true}
                  />
                </Grid>
                <Grid size={12}>
                  <FieldText
                    control={control}
                    name={'email'}
                    label={'Email'}
                    disabled={true}
                  />
                </Grid>
                <Grid size={12}>
                  <FieldImage
                    getValues={getValues}
                    watch={watch}
                    control={control}
                    name={'photoURL'}
                    label={'Photo'}
                    disabled={true}
                  />
                </Grid>
                <Grid size={12}>
                  <FieldText
                    control={control}
                    name={'dateSignIn'}
                    label={'Sign in Date'}
                    disabled={true}
                  />
                </Grid>
                <Grid size={12}>
                  <FieldSelect
                    options={userRoles}
                    control={control}
                    name={'role'}
                    label={'Role'}
                    disabled={stateForm === 'edit'}
                    disableClear={true}
                    setValue={setValue}
                  />
                </Grid>
                <Grid size={12}>
                  <FieldCurrency
                    control={control}
                    name={'sequenceTotal'}
                    label={'Sequence total'}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        {portalRef.current &&
          createPortal(
            <>
              <Grid
                container
                spacing={3}
                justifyContent="flex-end"
              >
                {stateForm === 'edit' && permissions?.users?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Edit />}
                      onClick={editUser}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Edit
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.users?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Undo />}
                      onClick={revertUser}
                      size={isMobile ? 'small' : 'large'}
                      disabled={stateSaving}
                    >
                      Undo
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.users?.delete && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Delete />}
                      onClick={handleDialogDelete}
                      size={isMobile ? 'small' : 'large'}
                      disabled={stateSaving}
                    >
                      Delete
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.users?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Save />}
                      onClick={saveUser}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Save
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>,
            portalRef.current,
          )}
      </>
    </Box>
  );
};

export default FormUser;
